import React, { useContext } from "react";
import { AuthContext, IntlContext } from "context";
import { AdInterface } from "types/ad";
import { Shield, Check, Award } from "lucide-react";
import AdPrimaryAction from "./AdPrimaryAction";
import { useAdService } from "./useAdService";
import { Role } from "types/user";

type Props = {
    ad: AdInterface;
};

const AdSidebar: React.FC<Props> = ({ ad }) => {
    const { account, currentRole } = useContext(AuthContext);
    const intl = useContext(IntlContext);
    const providerId = account?.resources?.provider?.[0]?.id;
    const { hasService, serviceId } = useAdService(account, ad);
    const isVerified = ad.client.dataleon_status === "approved";
    const isOwnAd = Boolean(account?.user?.id && ad.client?.user_id === account.user.id);
    const activeProviderApplications = ad.providers_active_apply ?? ad.providers_apply;
    const alreadyApplied = providerId ? activeProviderApplications.includes(providerId) : false;
    const applicationsCount = ad.providers_apply?.length ?? 0;
    const isProvider = currentRole === Role.PROVIDER;
    const isClient = currentRole === Role.CLIENT;

    if (isClient && !isOwnAd) return null;

    return (
        <div className="lg:col-span-1 space-y-4 lg:sticky lg:top-[80px] lg:self-start">
            {/* application card */}
            <div className="bg-white rounded-[32px] border border-[#f1f5f9] drop-shadow-[0px_10px_20px_rgba(0,0,0,0.04)] p-6 flex flex-col gap-4">
                {/* primary action: apply */}
                <AdPrimaryAction
                    isProvider={isProvider}
                    isOwnAd={isOwnAd}
                    alreadyApplied={alreadyApplied}
                    adClientId={ad.client_id}
                    hasService={hasService}
                    applicationsCount={applicationsCount}
                    adId={ad.id}
                    serviceId={serviceId}
                />

                {/* trust items */}
                <div className="border-t border-[#f1f5f9] pt-4 flex flex-col gap-3">
                    <div className="flex items-center gap-3">
                        <div className="shrink-0 w-5 h-5 text-[#4c4cf4]">
                            <Shield className="w-5 h-5" />
                        </div>
                        <span className="text-[13px] font-medium text-[#5a607c]">
                            {intl.formatMessage("ad.view.trust_payment")}
                        </span>
                    </div>
                    {isVerified && (
                        <div className="flex items-center gap-3">
                            <div className="shrink-0 w-5 h-5">
                                <Check className="w-5 h-5 text-[#22c55e]" />
                            </div>
                            <span className="text-[13px] font-medium text-[#5a607c]">
                                {intl.formatMessage("ad.view.trust_verified")}
                            </span>
                        </div>
                    )}
                    <div className="flex items-center gap-3">
                        <div className="shrink-0 w-5 h-5">
                            <Award className="w-5 h-5 text-[#f59e0b]" />
                        </div>
                        <span className="text-[13px] font-medium text-[#5a607c]">
                            {intl.formatMessage("ad.view.trust_satisfaction")}
                        </span>
                    </div>
                </div>
            </div>

            {/* safety tips card */}
            <div className="bg-[#fffbeb] border border-[#fef3c6] rounded-[24px] pt-[25px] px-[25px] pb-[25px] flex flex-col gap-4">
                <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-[#fef3c6] rounded-[14px] flex items-center justify-center shrink-0">
                        <Shield className="w-4 text-[#e17100]" />
                    </div>
                    <h3 className="text-[13px] font-black text-[#7b3306] uppercase tracking-[1.3px]">
                        {intl.formatMessage("ad.view.safety_tips_title")}
                    </h3>
                </div>
                <ul className="flex flex-col gap-2">
                    <li className="flex items-start gap-[14px]">
                        <span className="text-[#e17100] text-[13px] font-medium leading-[19.5px] shrink-0">•</span>
                        <span className="text-[13px] font-medium text-[#7b3306] leading-[19.5px]">
                            {intl.formatMessage("ad.view.safety_tips_verified_profile")}
                        </span>
                    </li>
                    <li className="flex items-start gap-[14px]">
                        <span className="text-[#e17100] text-[13px] font-medium leading-[19.5px] shrink-0">•</span>
                        <span className="text-[13px] font-medium text-[#7b3306] leading-[19.5px]">
                            {intl.formatMessage("ad.view.safety_tips_secure_payment")}
                        </span>
                    </li>
                    <li className="flex items-start gap-[14px]">
                        <span className="text-[#e17100] text-[13px] font-medium leading-[19.5px] shrink-0">•</span>
                        <span className="text-[13px] font-medium text-[#7b3306] leading-[19.5px]">
                            {intl.formatMessage("ad.view.safety_tips_insurance")}
                        </span>
                    </li>
                </ul>
            </div>
        </div>
    );
};

export default AdSidebar;
